/**
 * OperationChecklistSheet.js
 *
 * Bottom sheet modal for operation-specific checklists
 * Shown when starting an operation (Crew Transfer, Pilot Transfer, etc.)
 *
 * Features:
 * - Dynamic form fields based on operation type
 * - Safety checks (optional, can skip)
 * - Risk Assessment status for Towing
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  X,
  Users,
  UserCheck,
  Clock,
  Link as LinkIcon,
  Waves,
  Anchor,
  CheckSquare,
  Square,
  AlertTriangle,
  CheckCircle,
  ExternalLink
} from 'lucide-react';

// Common components
import Dropdown from '../../../../common/Dropdown';

// Config
import {
  OPERATION_TYPES,
  OPERATION_TYPE_LABELS,
  OPERATION_CHECKLISTS
} from '../../../../../config/operationsConfig';

// Operation icons map
const OPERATION_ICONS = {
  [OPERATION_TYPES.CREW_TRANSFER]: Users,
  [OPERATION_TYPES.PILOT_TRANSFER]: UserCheck,
  [OPERATION_TYPES.STANDBY]: Clock,
  [OPERATION_TYPES.TOWING]: LinkIcon,
  [OPERATION_TYPES.DIVING_SUPPORT]: Waves,
  [OPERATION_TYPES.ANCHORING]: Anchor
};

/**
 * OperationChecklistSheet Component
 */
const OperationChecklistSheet = ({
  operationType,
  onStart,
  onCancel,
  hasApprovedRA = false, // For towing - whether an approved RA exists
  raTitle = null,
  raLastReviewed = null,
  onViewRA = null
}) => {
  const checklist = OPERATION_CHECKLISTS[operationType];
  const Icon = OPERATION_ICONS[operationType] || Users;
  const label = OPERATION_TYPE_LABELS[operationType] || operationType;

  // Form state
  const [fieldValues, setFieldValues] = useState(() => {
    if (!checklist?.fields) return {};
    const initial = {};
    checklist.fields.forEach(field => {
      initial[field.id] = field.type === 'number' ? '' : '';
    });
    return initial;
  });

  // Safety checks state
  const [checks, setChecks] = useState(() => {
    if (!checklist?.checks) return [];
    return checklist.checks.map(check => ({
      ...check,
      completed: false
    }));
  });

  // Validate required fields
  const isValid = useMemo(() => {
    if (!checklist?.fields) return true;
    return checklist.fields
      .filter(f => f.required)
      .every(f => {
        const value = fieldValues[f.id];
        return value !== '' && value !== undefined && value !== null;
      });
  }, [checklist, fieldValues]);

  // Handle field change
  const handleFieldChange = useCallback((fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Handle check toggle
  const handleCheckToggle = useCallback((checkId) => {
    setChecks(prev => prev.map(check =>
      check.id === checkId
        ? { ...check, completed: !check.completed }
        : check
    ));
  }, []);

  // Handle start operation
  const handleStart = useCallback(() => {
    const operationData = {
      ...fieldValues,
      checks: checks.map(c => ({ id: c.id, completed: c.completed })),
      checklistCompleted: checks.some(c => c.completed)
    };
    onStart(operationData);
  }, [fieldValues, checks, onStart]);

  // Handle skip (start without filling checklist)
  const handleSkip = useCallback(() => {
    onStart({ checklistSkipped: true });
  }, [onStart]);

  // Render field based on type
  const renderField = (field) => {
    switch (field.type) {
      case 'select':
        return (
          <Dropdown
            value={fieldValues[field.id] || ''}
            onChange={(val) => handleFieldChange(field.id, val)}
            options={field.options || []}
            placeholder="Select..."
            width="w-full"
          />
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map(opt => (
              <label
                key={opt.value}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  fieldValues[field.id] === opt.value
                    ? 'border-cyan-500 bg-cyan-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name={field.id}
                  value={opt.value}
                  checked={fieldValues[field.id] === opt.value}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="h-4 w-4 text-cyan-600"
                />
                <span className="text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            inputMode="numeric"
            value={fieldValues[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder || '0'}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            style={{ minHeight: '48px' }}
          />
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            value={fieldValues[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            style={{ minHeight: '48px' }}
          />
        );
    }
  };

  // If no checklist exists for this operation type, just start it
  if (!checklist) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      <div className="relative w-full max-w-lg bg-white rounded-t-xl sm:rounded-xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Icon className="h-5 w-5 text-cyan-600" />
            </div>
            <h3 className="font-semibold text-gray-900">{label}</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Risk Assessment Status (for Towing) */}
          {checklist.requiresRA && (
            <div className={`mb-4 p-3 rounded-lg border ${
              hasApprovedRA
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
              {hasApprovedRA ? (
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-green-800">Approved Towing RA exists</p>
                    {raTitle && <p className="text-sm text-green-700 mt-0.5">{raTitle}</p>}
                    {raLastReviewed && (
                      <p className="text-xs text-green-600 mt-1">
                        Last reviewed: {new Date(raLastReviewed).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {onViewRA && (
                    <button
                      onClick={onViewRA}
                      className="text-sm text-green-700 hover:text-green-800 flex items-center gap-1"
                    >
                      View RA
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">No approved Towing RA found</p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      WBC3 26.3.1 requires risk assessment for every towing voyage.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form Fields */}
          {checklist.fields && checklist.fields.length > 0 && (
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                {label} Details
              </h4>
              {checklist.fields.map(field => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </div>
          )}

          {/* Safety Checks */}
          {checks.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                Safety Checks
              </h4>
              <div className="space-y-2">
                {checks.map(check => (
                  <button
                    key={check.id}
                    onClick={() => handleCheckToggle(check.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 text-left transition-colors"
                  >
                    {check.completed ? (
                      <CheckSquare className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <Square className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={check.completed ? 'text-gray-500' : 'text-gray-900'}>
                      {check.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-3 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            onClick={handleSkip}
            className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
            style={{ minHeight: '48px' }}
          >
            Skip Checks
          </button>
          <button
            onClick={handleStart}
            disabled={!isValid}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
              isValid
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
            style={{ minHeight: '48px' }}
          >
            Start {label.split(' ')[0]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperationChecklistSheet;
